/**
 * Preisempfehlungen aus den Zahlen, die ohnehin schon im Haus sind.
 *
 * WARUM es das gibt: der häufigste Preisfehler eines kleinen Vermieters ist kein
 * falscher Betrag, sondern ein fehlender — eine Woche, in der drei Bundesländer
 * gleichzeitig Ferien haben und trotzdem der Grundpreis greift. Das sieht man im
 * Kalender nicht, weil dort die Buchungen stehen und nicht das, was fehlt.
 *
 * WAS DAS HIER NICHT IST: kein Modell, keine Prognose, kein Marktpreis. Es sind drei
 * Kennzahlen pro Kalenderwoche — Auslastung, Ferienlage, aktueller Durchschnittspreis —
 * und eine Regel darüber. Die Prozentsätze weiter unten sind Erfahrungswerte und als
 * solche benannt; sie sind ausdrücklich KEIN Rechenergebnis, und die Karte muss das
 * auch so sagen. Ein „+14,7 %" wäre erfunden und würde Vertrauen kosten, das die
 * ehrliche Variante nicht braucht.
 *
 * WARUM ZEITRÄUME UND NICHT WOCHEN: eine Saison ist im Kopf des Vermieters kein
 * Kalenderwochen-Raster, sondern „die Sommerferien". Drei Hinweise für KW 30, 31 und 32
 * beschreiben denselben Sachverhalt dreimal und lassen die Arbeit — den Zeitraum
 * zusammenzusetzen — beim Leser. Deshalb werden benachbarte, gleichartige Wochen zu
 * einem Vorschlag verschmolzen (`groupSpans`), und aus dem Vorschlag lässt sich mit
 * einem Klick eine echte `rate_periods`-Zeile machen.
 *
 * WARUM DER WOCHENENDZUSCHLAG SEPARAT STEHT: er ist keine Frage des Zeitraums, sondern
 * des Wochentags, und er wird aus der VERGANGENHEIT belegt statt aus dem Buchungsstand
 * der Zukunft — siehe `buildWeekendAdvice`.
 *
 * Reine Funktionen, kein React, kein Datenzugriff — getestet in priceAdvice.test.ts.
 */

import type { Booking, ISODate, RatePeriod, Unit } from '../data/model'
import { BLOCKING_STATUSES } from '../data/model'
import { addDays, addMonths, isoWeekday, nights } from './dates'
import { resolveNightlyRate } from './pricing'

/* --------------------------------------------------------------- Schwellenwerte
 *
 * Alle Grenzen stehen hier oben, damit man sie diskutieren kann, ohne die Logik zu
 * lesen. Jede hat einen Grund; wo der Grund eine Faustregel ist, steht das dabei.
 */

/** Wie weit voraus gerechnet wird. Weiter reichen weder Saisonpreise noch Buchungen. */
export const HORIZON_MONTHS = 6

const DAYS_PER_WEEK = 7

/**
 * Ab hier ist die Woche voll genug, dass die restlichen Nächte Preismacht haben.
 * Bei 75 % sind von sechs Einheiten im Schnitt anderthalb frei — wer dann noch anfragt,
 * hat wenig Alternativen. Darunter kostet eine Erhöhung eher eine Buchung, als sie
 * Ertrag bringt.
 */
const HIGH_OCCUPANCY = 0.75

/** Praktisch ausgebucht. Rechtfertigt den oberen Rand des Vorschlags. */
const VERY_HIGH_OCCUPANCY = 0.9

/**
 * Darunter ist die Woche eine Lücke. 35 % heißt: zwei von drei Nächten stehen leer,
 * das ist keine normale Streuung mehr.
 */
const LOW_OCCUPANCY = 0.35

/**
 * Ab wann eine Woche „auf Ferien" ist: die Hälfte der beobachteten Gästemärkte, über
 * die Woche gemittelt. Ein einzelner Feiertagsdonnerstag in einem von sieben Märkten
 * erreicht das nicht — und soll es auch nicht.
 */
const HIGH_HOLIDAY_DENSITY = 0.5

/**
 * Wie nah eine Lücke sein muss, um sie zu melden. Sechs Wochen sind ungefähr die
 * Vorlaufzeit, in der Direktbuchungen für einen Kurzurlaub noch entstehen; was weiter
 * weg ist, füllt sich in aller Regel von selbst und wäre nur Lärm.
 */
const NEAR_TERM_DAYS = 42

/**
 * Ab welchem Anteil des Jahresmittels der Preis als „schon hoch" gilt und keine
 * Erhöhung mehr vorgeschlagen wird. 10 % Toleranz, damit ein Wochenendzuschlag die
 * Empfehlung nicht schon abschaltet.
 */
const PRICE_ALREADY_ABOVE_MEAN = 1.1

/** Anteil der Nächte ohne passende Saison, ab dem die Woche als „Grundpreis" gilt. */
const BASE_RATE_SHARE_FOR_NO_SEASON = 1

/* Der vorgeschlagene Aufschlag. Faustwerte, keine Berechnung — siehe Kopf der Datei. */
const RAISE_BASE_PCT = 10
const RAISE_HOLIDAY_BONUS_PCT = 5
const RAISE_FULL_BONUS_PCT = 5

/** Eine Ferienregion wird nur genannt, wenn sie die Mehrheit der Woche abdeckt. */
const REGION_MIN_DAYS = 4

/** Mehr als drei Regionen in einem Satz liest niemand. */
const MAX_REGIONS = 3

/**
 * Wie viele Vorschläge die Karte höchstens zeigt. Eine Liste mit sechsundzwanzig
 * Zeilen ist keine Empfehlung mehr, sondern eine Tabelle.
 */
export const MAX_SUGGESTIONS = 5

/* ------------------------------------------------- Zusammenhängende Zeiträume
 *
 * Wann sind zwei benachbarte Wochen „gleichartig"? Die Frage ist nicht statistisch,
 * sondern praktisch: es sind genau die Wochen, für die derselbe eine Saisonpreis
 * richtig wäre. Daraus folgen die drei Abbruchgründe unten — abweichender Preis,
 * abweichende Auslastung, zu lange Strecke.
 */

/**
 * Höchstlänge eines Vorschlags. Acht Wochen sind ungefähr die längsten Sommerferien,
 * die ein Gästemarkt hergibt — also die längste Strecke, die ein Vermieter noch als
 * EINE Saison denkt. Alles darüber ist keine Saison mehr, sondern die Preisliste, und
 * ein Vorschlag „nächste sechs Monate um 10 % teurer" ist genauso wertlos wie
 * sechsundzwanzig Einzelhinweise.
 */
const SPAN_MAX_WEEKS = 8

/**
 * Wie weit die heutigen Preise innerhalb eines Zeitraums auseinanderliegen dürfen.
 *
 * Eine neue Saison setzt EINEN Preis für die ganze Strecke. Wenn zwei benachbarte
 * Wochen heute 95 € und 140 € kosten, war das eine Entscheidung — sie in einen Topf
 * zu werfen würde sie stillschweigend kassieren. Ein Zehntel Toleranz, weil ein
 * Wochenendzuschlag den Wochenschnitt bereits um ein paar Prozent verschiebt.
 */
const SPAN_MAX_PRICE_SPREAD = 0.1

/**
 * Wie weit die Auslastung innerhalb eines Zeitraums streuen darf (in Prozentpunkten
 * als Anteil, 0,3 = 30 Punkte).
 *
 * Faustwert. Eine Woche mit 95 % und eine mit 55 % sind zwei verschiedene Lagen; der
 * Mittelwert von 75 % beschreibt keine von beiden und würde die volle Woche zu
 * billig und die halbleere zu teuer machen.
 */
const SPAN_MAX_OCCUPANCY_SPREAD = 0.3

/**
 * Ab welcher Laufzeit eine hinterlegte Saison keine Saison mehr ist, sondern der
 * Normalpreis. Vier Monate: alles Längere ist eine Ganzjahres- oder Halbjahreszeile,
 * gegen die eine Empfehlung sinnvoll bleibt.
 */
const DEDICATED_SEASON_MAX_DAYS = 120

/**
 * Ab welchem Anteil eigens bepreister Nächte eine Woche als „bewusst bepreist" gilt
 * und keinen Erhöhungsvorschlag mehr bekommt. Die Hälfte reicht: wer für den Zeitraum
 * eine eigene Saison geschrieben hat, hat über den Preis nachgedacht — dort weiter
 * „könnte höher sein" zu rufen, macht aus dem Hinweis eine Dauerbeschwerde. Nebenbei
 * ist das der Grund, warum ein Vorschlag nach dem Anlegen verschwindet.
 */
const DEDICATED_SEASON_MIN_SHARE = 0.5

/* -------------------------------------------------------- Wochenendzuschlag */

/** Bit 0 … Bit 6 gesetzt: gilt an allen sieben Tagen. */
export const ALL_DAYS_MASK = 127

/** Freitag- und Samstagnacht. Bit 4 und Bit 5, wie `isoWeekday` sie zählt. */
export const WEEKEND_DOW_MASK = (1 << 4) | (1 << 5)

/** Montag bis Donnerstag. */
const WEEKDAY_DOW_MASK = 0b1111

/**
 * Über welchen Zeitraum die Wochentagsauslastung gemessen wird — und zwar RÜCKWÄRTS.
 *
 * WARUM DIE VERGANGENHEIT: der Buchungsstand der Zukunft sagt vor allem etwas über
 * Vorlaufzeit aus. In fünf Monaten ist noch alles frei, an jedem Wochentag gleich —
 * der Wochenendvorsprung wäre dort systematisch wegverdünnt und der Vorschlag käme
 * nie. Was tatsächlich stattgefunden hat, ist dagegen echte Nachfrage.
 */
export const WEEKEND_LOOKBACK_MONTHS = 6

/**
 * Wie viele verfügbare Freitag-/Samstagnächte im Rückblick liegen müssen, damit die
 * Zahl etwas bedeutet. Zwanzig sind rund zehn Wochenenden — darunter entscheidet eine
 * einzelne Familie über die Empfehlung.
 */
const WEEKEND_MIN_SAMPLE_NIGHTS = 20

/**
 * Wie viel besser Fr/Sa laufen müssen, damit der Zuschlag ein Thema ist. Zwanzig
 * Punkte Vorsprung sind Faustwert: darunter ist es die normale Streuung eines
 * Kleinbetriebs mit drei Wohnungen, darüber ein Muster.
 */
const WEEKEND_MIN_ADVANTAGE = 0.2

/** Ab hier ist der Vorsprung so deutlich, dass der obere Rand gerechtfertigt ist. */
const WEEKEND_STRONG_ADVANTAGE = 0.35

/**
 * Wochenenden müssen außerdem für sich genommen gut laufen. 15 % gegen 0 % ist ein
 * Vorsprung, aber keine Preismacht — dort fehlen Gäste und nicht Euro.
 */
const WEEKEND_MIN_OCCUPANCY = 0.5

/* Aufschlag auf die Wochenendnacht. Faustwerte, wie oben — keine Berechnung. */
const WEEKEND_BASE_PCT = 10
const WEEKEND_STRONG_BONUS_PCT = 5

/**
 * Wie lange ein neu angelegter Wochenendzuschlag gilt. Ein Zuschlag ist keine Saison,
 * er gilt bis auf Weiteres — aber eine Zeile ohne Enddatum kann das Schema nicht, und
 * ein Jahr ist der Abstand, in dem ohnehin jeder Vermieter seine Preise ansieht.
 */
export const WEEKEND_RULE_MONTHS = 12

/**
 * Ab dieser Priorität leben Zuschläge, darunter die Saisonpreise.
 *
 * WARUM EINE FESTE GRENZE: bei gleicher Reichweite entscheidet in `resolveNightlyRate`
 * allein die Priorität. Würde eine neu angelegte Saison einfach „eins über dem
 * Höchsten" landen, überholte sie irgendwann den Wochenendzuschlag und der wäre
 * stillschweigend tot — genau die Sorte Überraschung, die niemand mehr aufklärt. Mit
 * dem Band bleibt die Regel in einem Satz erklärbar: Saison unter 100, Zuschlag ab 100.
 */
export const SURCHARGE_PRIORITY_FLOOR = 100

/* -------------------------------------------------------------------- Typen */

export type AdviceKind = 'raise' | 'gap' | 'no_season'

/** Der Ausschnitt aus `HolidayDay` (useTenantHolidays), den die Rechnung braucht. */
export interface HolidayDensityDay {
  /** Regionen mit Schulferien an diesem Tag, in der aktuellen Sprache. */
  school: string[]
  /** Anteil der beobachteten Märkte, die schulfrei haben. 0 … 1. */
  density: number
}

export interface WeekStats {
  /** Montag der Woche. */
  weekStart: ISODate
  /** Montag der Folgewoche — halboffen, wie jeder Zeitraum im Schema. */
  weekEnd: ISODate
  /** Kalenderwoche nach ISO 8601. */
  isoWeek: number
  /** Tage von heute bis zum Wochenbeginn. */
  leadDays: number
  /** Nächte, die überhaupt verkauft werden können: aktive Einheiten minus Sperren. */
  availableNights: number
  /** Belegte Nächte aus blockierenden Buchungen (Sperren zählen hier nicht mit). */
  occupiedNights: number
  /** 0 … 1. Null, wenn nichts verfügbar ist. */
  occupancy: number
  /** Mittlerer Nachtpreis laut Saisonpreisen über alle aktiven Einheiten. */
  averagePrice: number
  /** Anteil der Nächte, für die keine Saison greift und der Grundpreis zieht. */
  baseRateShare: number
  /**
   * Anteil der Nächte, für die eine eigens für diesen Zeitraum geschriebene Saison
   * greift (kurz genug, um keine Ganzjahreszeile zu sein). Hoch = hier hat jemand
   * bereits entschieden.
   */
  dedicatedRateShare: number
  /** Mittlere Ferienlage über die sieben Tage. 0 … 1. */
  holidayDensity: number
  /** Regionen mit Ferien in dieser Woche, die längste zuerst. */
  holidayRegions: string[]
}

export interface Advice extends WeekStats {
  kind: AdviceKind
  /** Vorgeschlagener Aufschlag in Prozent. Null, wenn nur eine Prüfung ansteht. */
  suggestedPct: number | null
  /** Preis dieser Woche im Verhältnis zum Mittel des Zeitraums. 1 = auf dem Mittel. */
  priceVsMean: number | null
}

/**
 * Ein zusammenhängender Zeitraum aus benachbarten, gleichartigen Wochen.
 *
 * Das ist die Einheit, in der die Oberfläche spricht und in der eine Saison angelegt
 * wird — nicht die Kalenderwoche.
 */
export interface AdviceSpan {
  kind: AdviceKind
  /** Montag der ersten Woche. */
  from: ISODate
  /** Montag nach der letzten Woche. Halboffen, wie jeder Zeitraum im Schema. */
  to: ISODate
  /** Letzter Tag, EINSCHLIESSLICH — so, wie `rate_periods.valid_to` gelesen wird. */
  lastDay: ISODate
  /** Die Wochen, aus denen der Zeitraum besteht. */
  weeks: Advice[]
  /** Tage von heute bis zum Beginn. */
  leadDays: number
  availableNights: number
  occupiedNights: number
  /** Nach verfügbaren Nächten gewichtet, nicht als Mittel der Wochenwerte. */
  occupancy: number
  averagePrice: number
  holidayDensity: number
  /** Regionen, die den überwiegenden Teil des Zeitraums abdecken. */
  holidayRegions: string[]
  /** Vorgeschlagener Aufschlag in Prozent. Null = nichts zu erhöhen. */
  suggestedPct: number | null
  /** Der Preis, der daraus folgt, auf ganze Währungseinheiten gerundet. */
  suggestedPrice: number | null
}

/** Auslastung eines einzelnen Wochentags über ein Zeitfenster. */
export interface DowOccupancy {
  /** 0 = Montag … 6 = Sonntag, wie `isoWeekday` und die `dow_mask`-Bits. */
  dow: number
  availableNights: number
  occupiedNights: number
  occupancy: number
}

/** Der Wochenendzuschlag — ein Vorschlag, der nicht am Zeitraum hängt. */
export interface WeekendAdvice {
  /** Alle sieben Wochentage, für die Begründung auf dem Schirm. */
  byWeekday: DowOccupancy[]
  /** Fr + Sa im Rückblick. */
  weekendOccupancy: number
  /** Mo–Do im Rückblick. */
  weekdayOccupancy: number
  /** Vorsprung in Anteilspunkten (0,25 = 25 Punkte). */
  advantage: number
  /** Verfügbare Fr/Sa-Nächte im Rückblick — die Belastbarkeit der Zahl. */
  sampleNights: number
  lookbackFrom: ISODate
  lookbackTo: ISODate
  /** Was Fr/Sa im kommenden Zeitraum heute kosten. */
  averagePrice: number
  suggestedPct: number
  suggestedPrice: number
  validFrom: ISODate
  /** Einschließlich. */
  validTo: ISODate
}

export interface PriceAdviceResult {
  /** Alle betrachteten Wochen, chronologisch. Auch die ohne Empfehlung. */
  weeks: WeekStats[]
  /** Die ausgewählten Vorschläge, chronologisch. */
  advice: Advice[]
  /** Dieselben Befunde, zu Zeiträumen zusammengefasst — was die Oberfläche zeigt. */
  spans: AdviceSpan[]
  /** Der Wochenendzuschlag, oder null wenn es keinen zu empfehlen gibt. */
  weekend: WeekendAdvice | null
  /** Mittlerer Nachtpreis über den ganzen Zeitraum — das „Jahresmittel" der Regel. */
  averagePrice: number
  /** Wie viele Wochen im Zeitraum ohne hinterlegte Saison sind (nicht nur die gezeigte). */
  noSeasonWeeks: number
  /** Ob überhaupt Feriendaten vorlagen. In der Demo immer false. */
  hasHolidayData: boolean
  activeUnits: number
}

export interface PriceAdviceInput {
  today: ISODate
  units: Unit[]
  bookings: Booking[]
  ratePeriods: RatePeriod[]
  /** `byDay` aus `useTenantHolidays`. Fehlt in der Demo und vor dem ersten Sync. */
  holidaysByDay?: ReadonlyMap<ISODate, HolidayDensityDay>
  horizonMonths?: number
  maxSuggestions?: number
}

/* ------------------------------------------------------------- Wochenrechnung */

/** Montag der Woche, in der `iso` liegt. */
export function mondayOf(iso: ISODate): ISODate {
  return addDays(iso, -isoWeekday(iso))
}

/**
 * Kalenderwoche nach ISO 8601.
 *
 * Der Donnerstag entscheidet, zu welchem Jahr die Woche gehört — deshalb wird das Jahr
 * an ihm abgelesen und nicht am übergebenen Datum. Der 1. Januar 2027 liegt so in
 * KW 53/2026, was er auch tut.
 */
export function isoWeekNumber(iso: ISODate): number {
  const monday = mondayOf(iso)
  const year = Number(addDays(monday, 3).slice(0, 4))
  // Der 4. Januar liegt per Definition immer in KW 1.
  const firstMonday = mondayOf(`${year}-01-04`)
  return Math.round(nights(firstMonday, monday) / DAYS_PER_WEEK) + 1
}

/**
 * Ob diese Saison eigens für einen Zeitraum geschrieben wurde.
 *
 * `valid_to` ist einschließlich, deshalb die zusätzliche Nacht. Eine Zeile, die von
 * Januar bis Dezember gilt, ist der Normalpreis und darf einem Vorschlag nicht im Weg
 * stehen; eine Zeile über drei Wochen ist eine Entscheidung.
 */
function isDedicatedSeason(p: RatePeriod): boolean {
  return nights(p.valid_from, p.valid_to) + 1 <= DEDICATED_SEASON_MAX_DAYS
}

/** Nächte, die `[from, to)` und `[lo, hi)` gemeinsam haben. */
function overlapNights(from: ISODate, to: ISODate, lo: ISODate, hi: ISODate): number {
  const a = from > lo ? from : lo
  const b = to < hi ? to : hi
  return a < b ? nights(a, b) : 0
}

/**
 * Kennzahlen für alle vollen Wochen des Zeitraums.
 *
 * Angefangen wird beim nächsten Montag, der nicht in der Vergangenheit liegt. Eine
 * bereits laufende Woche wird bewusst übersprungen: ihre Auslastung ist zur Hälfte
 * Geschichte, und ein Preis, der erst ab Mittwoch gilt, ändert an ihr nichts mehr.
 */
export function weeklyStats(input: PriceAdviceInput): WeekStats[] {
  const { today, ratePeriods, holidaysByDay } = input
  const units = input.units.filter((u) => u.active)
  const horizonEnd = addMonths(today, input.horizonMonths ?? HORIZON_MONTHS)
  if (units.length === 0) return []

  const unitIds = new Set(units.map((u) => u.id))
  const relevant = input.bookings.filter(
    (b) => unitIds.has(b.unit_id) && BLOCKING_STATUSES.includes(b.status),
  )

  const out: WeekStats[] = []
  let weekStart = mondayOf(today)
  if (weekStart < today) weekStart = addDays(weekStart, DAYS_PER_WEEK)

  for (; weekStart < horizonEnd; weekStart = addDays(weekStart, DAYS_PER_WEEK)) {
    const weekEnd = addDays(weekStart, DAYS_PER_WEEK)
    const days = Array.from({ length: DAYS_PER_WEEK }, (_, i) => addDays(weekStart, i))

    // Eine Sperre (Sanierung, Eigennutzung) ist keine Auslastung, sondern fehlende
    // Kapazität. Als „belegt" gezählt sähe die Woche voll aus und bekäme eine
    // Preiserhöhung für ein Bad, das gerade gefliest wird.
    let occupiedNights = 0
    let blockedNights = 0
    for (const b of relevant) {
      const n = overlapNights(b.arrival, b.departure, weekStart, weekEnd)
      if (n === 0) continue
      if (b.kind === 'block') blockedNights += n
      else occupiedNights += n
    }

    const capacity = units.length * DAYS_PER_WEEK
    const availableNights = Math.max(0, capacity - blockedNights)
    // Deckel: überlappende Importe aus zwei Portalen dürfen keine 110 % erzeugen.
    const occupied = Math.min(occupiedNights, availableNights)

    let priceSum = 0
    let priced = 0
    let baseNights = 0
    let dedicatedNights = 0
    for (const unit of units) {
      for (const day of days) {
        const r = resolveNightlyRate(unit, ratePeriods, day)
        priceSum += r.price
        priced += 1
        if (!r.rate) baseNights += 1
        else if (isDedicatedSeason(r.rate)) dedicatedNights += 1
      }
    }

    let densitySum = 0
    const regionDays = new Map<string, number>()
    for (const day of days) {
      const h = holidaysByDay?.get(day)
      if (!h) continue
      densitySum += h.density
      for (const region of h.school) {
        regionDays.set(region, (regionDays.get(region) ?? 0) + 1)
      }
    }
    const holidayRegions = [...regionDays.entries()]
      .filter(([, n]) => n >= REGION_MIN_DAYS)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, MAX_REGIONS)
      .map(([region]) => region)

    out.push({
      weekStart,
      weekEnd,
      isoWeek: isoWeekNumber(weekStart),
      leadDays: nights(today, weekStart),
      availableNights,
      occupiedNights: occupied,
      occupancy: availableNights > 0 ? occupied / availableNights : 0,
      averagePrice: priced > 0 ? priceSum / priced : 0,
      baseRateShare: priced > 0 ? baseNights / priced : 0,
      dedicatedRateShare: priced > 0 ? dedicatedNights / priced : 0,
      holidayDensity: densitySum / DAYS_PER_WEEK,
      holidayRegions,
    })
  }

  return out
}

/* ------------------------------------------------------- Wochentagsrechnung */

/**
 * Auslastung je Wochentag über `[from, to)`.
 *
 * Gezählt wird die NACHT, nicht der Tag: die Freitagnacht ist die Nacht, die auf einen
 * Freitag folgt — dieselbe Lesart, die `resolveNightlyRate` und die `dow_mask`
 * verwenden. Sonst wäre ein Wochenendzuschlag genau um einen Tag verschoben.
 *
 * Sperren mindern wie überall die Kapazität, statt als Belegung zu zählen.
 */
export function occupancyByWeekday(args: {
  units: Unit[]
  bookings: Booking[]
  from: ISODate
  to: ISODate
}): DowOccupancy[] {
  const units = args.units.filter((u) => u.active)
  const unitIds = new Set(units.map((u) => u.id))
  const relevant = args.bookings.filter(
    (b) => unitIds.has(b.unit_id) && BLOCKING_STATUSES.includes(b.status),
  )

  const available = new Array(DAYS_PER_WEEK).fill(0) as number[]
  const occupied = new Array(DAYS_PER_WEEK).fill(0) as number[]
  if (units.length === 0 || args.to <= args.from) {
    return available.map((_, dow) => ({ dow, availableNights: 0, occupiedNights: 0, occupancy: 0 }))
  }

  // Je Einheit und Nacht höchstens ein Zustand. Zwei Portalimporte, die dieselbe Nacht
  // doppelt melden, dürfen die Auslastung nicht über 100 % treiben.
  const state = new Map<string, 'block' | 'booked'>()
  for (const b of relevant) {
    const lo = b.arrival > args.from ? b.arrival : args.from
    const hi = b.departure < args.to ? b.departure : args.to
    for (let d = lo; d < hi; d = addDays(d, 1)) {
      const key = `${b.unit_id}|${d}`
      if (b.kind === 'block') state.set(key, 'block')
      else if (state.get(key) !== 'block') state.set(key, 'booked')
    }
  }

  for (let d = args.from; d < args.to; d = addDays(d, 1)) {
    const dow = isoWeekday(d)
    for (const u of units) {
      const s = state.get(`${u.id}|${d}`)
      if (s === 'block') continue
      available[dow]! += 1
      if (s === 'booked') occupied[dow]! += 1
    }
  }

  return available.map((n, dow) => ({
    dow,
    availableNights: n,
    occupiedNights: occupied[dow]!,
    occupancy: n > 0 ? occupied[dow]! / n : 0,
  }))
}

/**
 * Auslastung über die Wochentage einer `dow_mask`, nach Nächten gewichtet.
 *
 * Gewichtet und nicht als Mittel der Tageswerte: ein Feiertagsmontag mit einer
 * einzigen verfügbaren Nacht darf nicht so schwer wiegen wie ein voller Freitag.
 */
function occupancyOf(rows: DowOccupancy[], mask: number): { occupancy: number; nights: number } {
  let av = 0
  let oc = 0
  for (const r of rows) {
    if ((mask & (1 << r.dow)) === 0) continue
    av += r.availableNights
    oc += r.occupiedNights
  }
  return { occupancy: av > 0 ? oc / av : 0, nights: av }
}

/**
 * Ob der Betrieb bereits nach Wochentagen unterscheidet.
 *
 * Bewusst weit gefasst: nicht nur „gilt an Fr und Sa", sondern JEDE aktive Zeile, die
 * nicht an allen sieben Tagen gilt. Ein Montag-bis-Donnerstag-Rabatt ist derselbe
 * Hebel von der anderen Seite, und ein Vorschlag für etwas, das schon existiert,
 * kostet das Vertrauen in alle anderen Vorschläge gleich mit.
 *
 * Geprüft wird nur, was den betrachteten Zeitraum überhaupt berührt — eine abgelaufene
 * Zeile aus dem Vorjahr sagt über die kommenden Monate nichts.
 */
export function hasWeekdayRate(periods: RatePeriod[], from: ISODate, lastDay: ISODate): boolean {
  return periods.some(
    (p) =>
      p.active &&
      p.dow_mask !== ALL_DAYS_MASK &&
      p.valid_from <= lastDay &&
      p.valid_to >= from,
  )
}

/**
 * Der Wochenendzuschlag, falls einer angebracht ist.
 *
 * Vier Bedingungen, alle müssen gelten: genug Belege, deutlicher Vorsprung, Fr/Sa für
 * sich genommen gut gebucht, und noch keine Zeile, die nach Wochentagen unterscheidet.
 *
 * Der Sonntag zählt in keine der beiden Gruppen. Er ist die Abreisenacht der
 * Wochenendgäste und die Anreisenacht der Wochengäste — in beiden Töpfen würde er das
 * Bild verwischen, das die Empfehlung trägt.
 */
export function buildWeekendAdvice(input: PriceAdviceInput): WeekendAdvice | null {
  const { today } = input
  const units = input.units.filter((u) => u.active)
  if (units.length === 0) return null

  const lookbackFrom = addMonths(today, -WEEKEND_LOOKBACK_MONTHS)
  const byWeekday = occupancyByWeekday({
    units, bookings: input.bookings, from: lookbackFrom, to: today,
  })

  const weekend = occupancyOf(byWeekday, WEEKEND_DOW_MASK)
  const weekday = occupancyOf(byWeekday, WEEKDAY_DOW_MASK)
  const advantage = weekend.occupancy - weekday.occupancy

  if (weekend.nights < WEEKEND_MIN_SAMPLE_NIGHTS) return null
  if (weekend.occupancy < WEEKEND_MIN_OCCUPANCY) return null
  if (advantage < WEEKEND_MIN_ADVANTAGE) return null

  const validFrom = today
  const validTo = addDays(addMonths(today, WEEKEND_RULE_MONTHS), -1)
  if (hasWeekdayRate(input.ratePeriods, validFrom, validTo)) return null

  // Der heutige Fr/Sa-Preis, gemittelt über die Einheiten und den Geltungszeitraum
  // des künftigen Zuschlags — das ist die Zahl, auf die der Aufschlag rechnet.
  let priceSum = 0
  let priced = 0
  for (let d = validFrom; d <= validTo; d = addDays(d, 1)) {
    if ((WEEKEND_DOW_MASK & (1 << isoWeekday(d))) === 0) continue
    for (const u of units) {
      priceSum += resolveNightlyRate(u, input.ratePeriods, d).price
      priced += 1
    }
  }
  const averagePrice = priced > 0 ? priceSum / priced : 0
  if (averagePrice <= 0) return null

  const suggestedPct =
    WEEKEND_BASE_PCT + (advantage >= WEEKEND_STRONG_ADVANTAGE ? WEEKEND_STRONG_BONUS_PCT : 0)

  return {
    byWeekday,
    weekendOccupancy: weekend.occupancy,
    weekdayOccupancy: weekday.occupancy,
    advantage,
    sampleNights: weekend.nights,
    lookbackFrom,
    lookbackTo: today,
    averagePrice,
    suggestedPct,
    suggestedPrice: Math.round(averagePrice * (1 + suggestedPct / 100)),
    validFrom,
    validTo,
  }
}

/* ---------------------------------------------------------------- Priorität */

/**
 * Die Priorität, mit der ein neu angelegter Preis eingehängt wird.
 *
 * `resolveNightlyRate` entscheidet bei gleicher Reichweite allein über die Priorität —
 * ein Vorschlag mit der falschen Zahl tut entweder nichts oder überfährt etwas
 * Handgemachtes, und beides versteht hinterher niemand mehr.
 *
 * Deshalb zwei Bänder (siehe `SURCHARGE_PRIORITY_FLOOR`):
 *  - Eine Saison (alle sieben Tage) legt sich eine Stufe über die Ganzjahreszeilen,
 *    die denselben Zeitraum bepreisen — gerade genug zum Gewinnen, und ohne je in das
 *    Band der Zuschläge zu geraten.
 *  - Ein Zuschlag (eingeschränkte `dow_mask`) legt sich über ALLES, was den Zeitraum
 *    bepreist, mindestens aber auf die Bandgrenze. Er ist ein Aufschlag auf die
 *    Saison, kein Konkurrent — und bleibt es auch, wenn später Saisons dazukommen.
 *
 * Betrachtet werden nur hausweite Zeilen: eine Ausnahme für eine einzelne Einheit
 * gewinnt in `resolveNightlyRate` ohnehin über die Reichweite und darf hier keine
 * Prioritätsspirale auslösen.
 */
export function proposedPriority(
  periods: RatePeriod[], from: ISODate, lastDay: ISODate, dowMask: number,
): number {
  const overlapping = periods.filter(
    (p) =>
      p.active &&
      p.unit_id == null &&
      p.category_id == null &&
      p.valid_from <= lastDay &&
      p.valid_to >= from,
  )

  if (dowMask !== ALL_DAYS_MASK) {
    const top = Math.max(SURCHARGE_PRIORITY_FLOOR - 1, ...overlapping.map((p) => p.priority))
    return top + 1
  }

  const seasons = overlapping.filter(
    (p) => p.dow_mask === ALL_DAYS_MASK && p.priority < SURCHARGE_PRIORITY_FLOOR,
  )
  const top = Math.max(-1, ...seasons.map((p) => p.priority))
  return Math.min(top + 1, SURCHARGE_PRIORITY_FLOOR - 1)
}

/* ------------------------------------------------------------------- Regeln */

/**
 * Wie dringend ein Vorschlag ist. Nur zum Sortieren; die Zahl steht nirgends auf dem
 * Schirm, weil sie niemandem etwas sagt.
 *
 * Die Reihenfolge der Bänder ist eine Aussage über den Wert: eine Woche ohne Saison
 * kostet jede Nacht Geld, eine zu billige Ferienwoche kostet die Differenz, eine Lücke
 * kostet nur, wenn sie leer bleibt.
 */
function score(kind: AdviceKind, w: WeekStats): number {
  switch (kind) {
    case 'no_season':
      return 3 + w.occupancy
    case 'raise':
      return 1 + (w.occupancy + w.holidayDensity) / 2
    case 'gap':
      return (1 - w.occupancy) * (1 - w.leadDays / (NEAR_TERM_DAYS + 1))
  }
}

function classify(w: WeekStats, priceVsMean: number | null): AdviceKind | null {
  // Zuerst der Grundpreis-Fall: solange keine Saison greift, ist jede Feinjustierung
  // am Prozentsatz Kosmetik an der falschen Zahl.
  if (w.baseRateShare >= BASE_RATE_SHARE_FOR_NO_SEASON) return 'no_season'

  // Wer für diese Woche schon eine eigene Saison geschrieben hat, hat den Preis
  // entschieden. Ohne diese Bedingung bliebe der Vorschlag nach dem Anlegen der
  // Saison oft stehen — der Aufschlag hebt ja auch das Mittel, gegen das gemessen
  // wird — und ein Knopf, der sich selbst nicht abräumt, wirkt kaputt.
  if (
    w.occupancy >= HIGH_OCCUPANCY &&
    w.dedicatedRateShare < DEDICATED_SEASON_MIN_SHARE &&
    (priceVsMean === null || priceVsMean <= PRICE_ALREADY_ABOVE_MEAN)
  ) {
    return 'raise'
  }

  if (w.occupancy <= LOW_OCCUPANCY && w.leadDays <= NEAR_TERM_DAYS) return 'gap'

  return null
}

function raisePct(w: WeekStats): number {
  let pct = RAISE_BASE_PCT
  if (w.holidayDensity >= HIGH_HOLIDAY_DENSITY) pct += RAISE_HOLIDAY_BONUS_PCT
  if (w.occupancy >= VERY_HIGH_OCCUPANCY) pct += RAISE_FULL_BONUS_PCT
  return pct
}

/**
 * Die Empfehlung.
 *
 * Wochen in der Vergangenheit und Wochen ohne verfügbare Nächte kommen nicht vor —
 * zur ersten kann man nichts mehr sagen, zur zweiten nichts.
 */
export function buildPriceAdvice(input: PriceAdviceInput): PriceAdviceResult {
  const weeks = weeklyStats(input)
  const activeUnits = input.units.filter((u) => u.active).length
  const hasHolidayData = (input.holidaysByDay?.size ?? 0) > 0

  const withData = weeks.filter((w) => w.availableNights > 0)
  const averagePrice =
    withData.length > 0
      ? withData.reduce((a, w) => a + w.averagePrice, 0) / withData.length
      : 0

  const candidates: { advice: Advice; rank: number }[] = []
  let noSeasonWeeks = 0

  for (const w of withData) {
    const priceVsMean = averagePrice > 0 ? w.averagePrice / averagePrice : null
    const kind = classify(w, priceVsMean)
    if (!kind) continue
    if (kind === 'no_season') noSeasonWeeks += 1
    candidates.push({
      advice: {
        ...w,
        kind,
        suggestedPct: kind === 'raise' ? raisePct(w) : null,
        priceVsMean,
      },
      rank: score(kind, w),
    })
  }

  candidates.sort((a, b) => b.rank - a.rank || a.advice.weekStart.localeCompare(b.advice.weekStart))

  // Fehlt die Saison ganz, gilt das für jede Woche gleichermaßen. Sechsundzwanzig Mal
  // denselben Satz zu zeigen macht ihn nicht wahrer — einmal, mit der Zahl daneben.
  const advice: Advice[] = []
  let seenNoSeason = false
  for (const c of candidates) {
    if (advice.length >= (input.maxSuggestions ?? MAX_SUGGESTIONS)) break
    if (c.advice.kind === 'no_season') {
      if (seenNoSeason) continue
      seenNoSeason = true
    }
    advice.push(c.advice)
  }
  advice.sort((a, b) => a.weekStart.localeCompare(b.weekStart))

  // Die Zeiträume entstehen aus ALLEN Befunden, nicht aus den fünf ausgewählten:
  // sonst würde die Ranglistenauswahl Wochen aus der Mitte reißen und aus einem
  // zusammenhängenden Sommer drei Stümpfe machen.
  const spans = rankSpans(
    groupSpans(candidates.map((c) => c.advice).sort(
      (a, b) => a.weekStart.localeCompare(b.weekStart)), averagePrice),
    input.maxSuggestions ?? MAX_SUGGESTIONS,
  )

  return {
    weeks, advice, spans, averagePrice, noSeasonWeeks, hasHolidayData, activeUnits,
    weekend: buildWeekendAdvice(input),
  }
}

/* ------------------------------------------------------ Zeiträume aus Wochen */

/**
 * Ob die Woche `next` noch in denselben Zeitraum gehört wie das bisher Gesammelte.
 *
 * Die vier Gründe zum Abbrechen sind bewusst so gewählt, dass am Ende genau die
 * Wochen zusammenstehen, für die EIN Saisonpreis die richtige Antwort wäre:
 *  1. andere Art — eine Lücke und eine volle Woche sind nicht dieselbe Maßnahme,
 *  2. eine Woche Abstand — ein Loch in der Mitte macht aus zwei Zeiträumen keinen,
 *  3. zu weit auseinanderliegende heutige Preise (`SPAN_MAX_PRICE_SPREAD`),
 *  4. zu weit auseinanderliegende Auslastung (`SPAN_MAX_OCCUPANCY_SPREAD`),
 *  5. zu lang (`SPAN_MAX_WEEKS`).
 */
function extendsSpan(open: Advice[], next: Advice): boolean {
  const last = open[open.length - 1]!
  if (open.length >= SPAN_MAX_WEEKS) return false
  if (last.kind !== next.kind) return false
  if (last.weekEnd !== next.weekStart) return false

  const prices = [...open, next].map((w) => w.averagePrice)
  const lowPrice = Math.min(...prices)
  const highPrice = Math.max(...prices)
  if (lowPrice > 0 && (highPrice - lowPrice) / lowPrice > SPAN_MAX_PRICE_SPREAD) return false

  const occupancies = [...open, next].map((w) => w.occupancy)
  if (Math.max(...occupancies) - Math.min(...occupancies) > SPAN_MAX_OCCUPANCY_SPREAD) {
    return false
  }

  return true
}

/**
 * Der Aufschlag für einen ganzen Zeitraum.
 *
 * Gerechnet wird auf den Kennzahlen des Zeitraums und nicht als Mittel der
 * Wochenvorschläge — ein Mittel aus 10 % und 20 % wäre 15 %, eine Zahl, die für keine
 * der beiden Wochen begründet ist.
 *
 * Auch ein `no_season`-Zeitraum bekommt einen Prozentsatz, sobald er gut gebucht ist:
 * das ist der Regelfall aus dem Alltag — die Sommerwochen laufen voll und es greift
 * trotzdem der Grundpreis. Ohne Auslastung bleibt es beim Hinweis, denn leere Wochen
 * teurer zu machen ist keine Empfehlung, sondern ein Fehler.
 */
function spanRaisePct(
  kind: AdviceKind, occupancy: number, holidayDensity: number,
  averagePrice: number, meanPrice: number,
): number | null {
  if (kind === 'gap') return null
  if (occupancy < HIGH_OCCUPANCY) return null
  if (meanPrice > 0 && averagePrice / meanPrice > PRICE_ALREADY_ABOVE_MEAN) return null

  let pct = RAISE_BASE_PCT
  if (holidayDensity >= HIGH_HOLIDAY_DENSITY) pct += RAISE_HOLIDAY_BONUS_PCT
  if (occupancy >= VERY_HIGH_OCCUPANCY) pct += RAISE_FULL_BONUS_PCT
  return pct
}

/**
 * Benachbarte, gleichartige Wochen zu Zeiträumen zusammenfassen.
 *
 * Erwartet die Wochen chronologisch. Ein Zeitraum aus einer einzigen Woche ist ein
 * gültiges Ergebnis — nicht jede Auffälligkeit hat Nachbarn.
 */
export function groupSpans(advice: Advice[], meanPrice: number): AdviceSpan[] {
  const groups: Advice[][] = []
  for (const a of advice) {
    const open = groups[groups.length - 1]
    if (open && extendsSpan(open, a)) open.push(a)
    else groups.push([a])
  }

  return groups.map((weeks) => {
    const first = weeks[0]!
    const last = weeks[weeks.length - 1]!
    const availableNights = weeks.reduce((n, w) => n + w.availableNights, 0)
    const occupiedNights = weeks.reduce((n, w) => n + w.occupiedNights, 0)
    const occupancy = availableNights > 0 ? occupiedNights / availableNights : 0
    const averagePrice = weeks.reduce((p, w) => p + w.averagePrice, 0) / weeks.length
    const holidayDensity = weeks.reduce((d, w) => d + w.holidayDensity, 0) / weeks.length

    // Eine Region wird nur genannt, wenn sie den überwiegenden Teil des Zeitraums
    // abdeckt — sonst stünde „Ferien in Bayern" über acht Wochen, von denen Bayern
    // eine hat.
    const regionWeeks = new Map<string, number>()
    for (const w of weeks) {
      for (const r of w.holidayRegions) regionWeeks.set(r, (regionWeeks.get(r) ?? 0) + 1)
    }
    const holidayRegions = [...regionWeeks.entries()]
      .filter(([, n]) => n * 2 >= weeks.length)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, MAX_REGIONS)
      .map(([region]) => region)

    const suggestedPct = spanRaisePct(
      first.kind, occupancy, holidayDensity, averagePrice, meanPrice)

    return {
      kind: first.kind,
      from: first.weekStart,
      to: last.weekEnd,
      lastDay: addDays(last.weekEnd, -1),
      weeks,
      leadDays: first.leadDays,
      availableNights,
      occupiedNights,
      occupancy,
      averagePrice,
      holidayDensity,
      holidayRegions,
      suggestedPct,
      // Auf ganze Währungseinheiten: „€ 108,63" liest sich wie ein Rechenergebnis,
      // „€ 109" wie ein Vorschlag — und genau das ist es.
      suggestedPrice:
        suggestedPct != null && averagePrice > 0
          ? Math.round(averagePrice * (1 + suggestedPct / 100))
          : null,
    }
  })
}

/**
 * Die wichtigsten Zeiträume, chronologisch.
 *
 * Der lange Zeitraum gewinnt bei gleichem Rang: er beschreibt mehr Nächte und damit
 * mehr Geld. Vom Grundpreis-Fall bleibt weiterhin nur einer übrig — er gilt sonst für
 * den ganzen Horizont und würde die Liste allein füllen.
 */
function rankSpans(spans: AdviceSpan[], max: number): AdviceSpan[] {
  const ranked = [...spans]
    .map((s) => ({
      span: s,
      rank: score(s.kind, {
        ...s.weeks[0]!, occupancy: s.occupancy, holidayDensity: s.holidayDensity,
      }),
    }))
    .sort((a, b) =>
      b.rank - a.rank ||
      b.span.weeks.length - a.span.weeks.length ||
      a.span.from.localeCompare(b.span.from))

  const out: AdviceSpan[] = []
  let seenNoSeason = false
  for (const r of ranked) {
    if (out.length >= max) break
    if (r.span.kind === 'no_season') {
      if (seenNoSeason) continue
      seenNoSeason = true
    }
    out.push(r.span)
  }
  return out.sort((a, b) => a.from.localeCompare(b.from))
}
